import i18n, { Trans } from "@mirage/translations";

import { IconButton } from "@dropbox/dig-components/buttons";
import { ProgressBar } from "@dropbox/dig-components/progress_indicators";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { InfoLine, WarningLine } from "@dropbox/dig-icons/assets";
import { TranscribeFileStatusError } from "@mirage/service-fss-bridge/service/bridge_types";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import css from "./TranscribingSourcesBanner.module.css";

export const TranscribingSourcesBanner = ({
  inProgress,
  fileName,
  errors,
}: {
  inProgress: number;
  fileName?: string;
  errors?: TranscribeFileStatusError[];
}) => {
  if (errors && errors.length > 0) {
    return <TranscribingSourcesErrorBanner errors={errors} />;
  }

  const loadingMessage = fileName ? (
    <>
      {i18n.t("transcribing_sources_banner_file_name")}
      <Text isBold className={css.message}>
        <Truncate location={1} observeParent>
          {fileName}
        </Truncate>
      </Text>
    </>
  ) : (
    <Trans
      i18nKey="transcribing_sources_banner"
      values={{
        count: inProgress,
      }}
      components={{
        bold: <b />,
      }}
    />
  );
  return (
    <div className={css.bannerContainer}>
      <Box className={css.banner}>
        <div className={css.actionContainer}>
          <Text variant="paragraph" size="medium" className={css.message}>
            {loadingMessage}
          </Text>
          <DigTooltip title={i18n.t("transcribing_sources_banner_tooltip")}>
            <Box as={IconButton} variant="transparent" flexShrink={0}>
              <UIIcon src={InfoLine} />
            </Box>
          </DigTooltip>
        </div>
      </Box>
      <ProgressBar className={css.progressBar} isIndeterminate={true} in />
    </div>
  );
};

const TranscribingSourcesErrorBanner = ({
  errors,
}: {
  errors: TranscribeFileStatusError[];
}) => {
  const errorMessage =
    errors.length === 1 ? (
      // Show file name if there is only one error
      <>
        {i18n.t("transcribing_sources_banner_error_file_name")}
        <Text isBold className={css.message}>
          <Truncate location={1} observeParent>
            {errors[0].file.name}
          </Truncate>
        </Text>
      </>
    ) : (
      // Show error count if there are multiple errors
      <Trans
        i18nKey="transcribing_sources_banner_error"
        values={{
          count: errors.length,
        }}
      />
    );

  const errorTooltipContent =
    errors.length === 1 ? errors[0].localizedError : undefined;

  return (
    <div className={css.bannerContainer}>
      <Box className={css.banner}>
        <div className={css.actionContainer}>
          <Text variant="paragraph" size="medium" className={css.message}>
            <Box
              as={UIIcon}
              src={WarningLine}
              flexShrink={0}
              marginRight="Micro Medium"
            />
            {errorMessage}
          </Text>
          {errorTooltipContent && (
            <DigTooltip title={errorTooltipContent}>
              <Box as={IconButton} variant="transparent" flexShrink={0}>
                <UIIcon src={InfoLine} />
              </Box>
            </DigTooltip>
          )}
        </div>
      </Box>
    </div>
  );
};
